// context/AIPersonalizeModalContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react'

interface ProductData {
  product: any
  inputs: any
  dedicationImage?: any
  isEdit?: string
  isProductRtl?: any
  use_ai_image_for_dedication?: any
}

interface ContextProps {
  isOpen: boolean
  productData: ProductData | null
  openAIModal: (data: ProductData) => void
  closeModal: () => void
  updateProductData: (updatedFields: Partial<ProductData['product']>) => void
  updateDedicationImage: (image: any) => void
  setProductRtl: (rtl: any) => void
}

const AIPersonalizeModalContext = createContext<ContextProps | undefined>(undefined)

export const AIPersonalizeModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [productData, setProductData] = useState<ProductData | null>(null)

  // Only load data from localStorage, but don't automatically open the modal
  useEffect(() => {
    const saved = localStorage.getItem('aiPersonalizeModal')
    if (saved) {
      const parsed = JSON.parse(saved)
      setProductData(parsed.productData)
    }
  }, [])

  useEffect(() => {
    if (isOpen && productData) {
      const { dedicationImage, ...rest } = productData
      localStorage.setItem(
        'aiPersonalizeModal',
        JSON.stringify({
          isOpen: true,
          productData: rest,
        })
      )
    } else {
      localStorage.removeItem('aiPersonalizeModal')
    }
  }, [isOpen, productData])

  const openAIModal = (data: ProductData) => {
    const normalizedData: ProductData = {
      ...data,
      isEdit: data.isEdit ?? '0',
      dedicationImage: data.dedicationImage ?? null,
      isProductRtl: data.isProductRtl ?? '0',
    }
    setProductData(normalizedData)
    setIsOpen(true)
  }

  const closeModal = () => {
    setProductData(null)
    setIsOpen(false)
    localStorage.removeItem('aiPersonalizeModal')
  }

  const updateProductData = (updatedFields: Partial<ProductData['product']>) => {
    setProductData((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        product: {
          ...prev.product,
          ...updatedFields,
        },
      }
    })
  }

  const updateDedicationImage = (image: any) => {
    setProductData((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        dedicationImage: image,
      }
    })
  }

  const setProductRtl = (rtl: boolean) => {
    setProductData((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        isProductRtl: rtl,
      }
    })
  }

  return (
    <AIPersonalizeModalContext.Provider
      value={{
        isOpen,
        productData,
        openAIModal,
        closeModal,
        updateProductData,
        updateDedicationImage,
        setProductRtl,
      }}
    >
      {children}
    </AIPersonalizeModalContext.Provider>
  )
}

export const useAIPersonalizeModal = () => {
  const context = useContext(AIPersonalizeModalContext)
  if (!context) {
    throw new Error('usePersonalizeModal must be used within PersonalizeModalProvider')
  }
  return context
}